// Primeros Pasos - Pre-Roadmap Interactivo de Configuración
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import './GettingStarted.css';

// Estado inicial del checklist
const initialSteps = {
    github: { completed: false, visitedAt: null },
    supabase: { completed: false, visitedAt: null },
    vercel: { completed: false, visitedAt: null },
    editor: { completed: false, choice: null, visitedAt: null } // choice: 'cursor' | 'antigravity'
};

const GettingStarted = () => {
    const [steps, setSteps] = useState(initialSteps);
    const [expandedStep, setExpandedStep] = useState(null);
    const [showCongrats, setShowCongrats] = useState(false);

    // Cargar progreso guardado
    useEffect(() => {
        const saved = localStorage.getItem('roadmap_onboarding_progress');
        if (saved) {
            try {
                setSteps(JSON.parse(saved));
            } catch (e) {
                console.error('Error loading progress:', e);
            }
        }
    }, []);

    // Guardar progreso
    useEffect(() => {
        localStorage.setItem('roadmap_onboarding_progress', JSON.stringify(steps));
        
        // Verificar si todo está completado
        const allDone = steps.github.completed && 
                       steps.supabase.completed && 
                       steps.vercel.completed && 
                       steps.editor.completed;
        setShowCongrats(allDone);
    }, [steps]);

    const markCompleted = (stepId, choice = null) => {
        setSteps(prev => ({
            ...prev,
            [stepId]: {
                ...prev[stepId],
                completed: true,
                completedAt: new Date().toISOString(),
                ...(choice && { choice })
            }
        }));
    };

    const resetProgress = () => {
        if (window.confirm('¿Seguro que quieres reiniciar tu progreso?')) {
            setSteps(initialSteps);
            localStorage.removeItem('roadmap_onboarding_progress');
        }
    };

    const completedCount = Object.values(steps).filter(s => s.completed).length;
    const progress = (completedCount / 4) * 100;

    return (
        <div className="getting-started-container">
            {/* Header */}
            <header className="gs-header">
                <Link to="/" className="gs-back">← Volver al inicio</Link>
                <div className="gs-logo">
                    <div className="gs-logo-icon">⚡</div>
                    <span>Roadmap Platform</span>
                </div>
            </header>

            {/* Hero */}
            <section className="gs-hero">
                <div className="gs-hero-content">
                    <h1>Preparemos tu espacio de trabajo</h1>
                    <p>
                        Antes de empezar a crear, necesitamos configurar las herramientas que usarás.
                        No te preocupes, te guiamos en cada paso. Ve a tu ritmo.
                    </p>
                    
                    {/* Progress bar */}
                    <div className="gs-progress-container">
                        <div className="gs-progress-bar">
                            <div 
                                className="gs-progress-fill" 
                                style={{ width: `${progress}%` }}
                            />
                        </div>
                        <span className="gs-progress-text">{completedCount} pasos listos</span>
                    </div>
                </div>

                {/* Mascota Roadmap - El líder */}
                <div className="gs-hero-mascot">
                    <div className="mascot-roadmap">
                        <div className="mascot-icon">🗺️</div>
                        <div className="mascot-glow"></div>
                    </div>
                    <p className="mascot-speech">
                        "Soy <strong>Roadmap</strong>, tu centro de operaciones. 
                        Te ayudaré a organizar tu proyecto y a no perder ninguna idea."
                    </p>
                </div>
            </section>

            {/* Objetivo, proceso y costes - explicación detallada para novatos */}
            <section className="gs-objective-costs">
                <h2>¿Qué es esto y qué te va a costar?</h2>
                <div className="gs-objective-block">
                    <h3>Objetivo de Roadmap Platform</h3>
                    <p>Esta herramienta sirve para que, si tienes una idea (app, tienda online, web de negocio) pero no sabes por dónde empezar, tengas un plan claro: un roadmap con fases y tareas. Puedes dictar ideas por voz, organizarlas y seguir el progreso. Nosotros te guiamos para conectar GitHub, Supabase, Vercel y un editor con IA (Cursor o Antigravity) para que puedas construir tu proyecto sin perderte.</p>
                </div>
                <div className="gs-process-block">
                    <h3>Proceso en resumen</h3>
                    <ol>
                        <li><strong>Cuenta:</strong> Te registras con GitHub y eliges plan (Basic o Pro).</li>
                        <li><strong>Pago:</strong> Pagas por Stripe (tarjeta). Sin prueba gratis; pago desde el primer día.</li>
                        <li><strong>Configuración:</strong> Los 4 pasos de esta página: GitHub, Supabase, Vercel y editor con IA. Te llevamos de la mano.</li>
                        <li><strong>Proyecto:</strong> Creas tu primer proyecto en el dashboard, describes la idea y obtienes un roadmap (o lo creas a mano).</li>
                        <li><strong>Construir:</strong> Usas el roadmap y el editor con IA para ir haciendo las tareas. Nosotros no programamos por ti; te damos el plan y la guía.</li>
                    </ol>
                </div>
                <div className="gs-costs-block">
                    <h3>Coste para empezar (resumen)</h3>
                    <p><strong>Con nosotros (Roadmap Platform):</strong></p>
                    <ul>
                        <li>Basic: 4,99 €/mes (1 proyecto, 1 usuario). Anual: 56,99 €/año (5% dto.).</li>
                        <li>Pro: 14,99 €/mes (proyectos ilimitados, hasta 10 usuarios por proyecto). Anual: 170,99 €/año (5% dto.).</li>
                    </ul>
                    <p><strong>Herramientas que usamos (pagas o gestionas tú):</strong></p>
                    <ul>
                        <li>GitHub: cuenta gratuita.</li>
                        <li>Supabase: plan gratuito hasta cierto uso.</li>
                        <li>Vercel: plan gratuito para proyectos personales.</li>
                        <li>Cursor (editor con IA): ~20 €/mes si quieres la versión Pro (opcional).</li>
                    </ul>
                    <p>Así sabes desde el principio qué te cuesta solo Roadmap Platform y qué pueden costar las herramientas con las que trabajamos. Sin sorpresas.</p>
                </div>
            </section>

            {/* Explicación del equipo */}
            <section className="gs-team-intro">
                <h2>Conoce al equipo</h2>
                <p>Estas son las herramientas que trabajarán contigo. Cada una tiene su función especial.</p>
                
                <div className="gs-team-grid">
                    <div className="team-member">
                        <div className="team-icon github">
                            <svg viewBox="0 0 24 24" fill="currentColor">
                                <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
                            </svg>
                        </div>
                        <h3>GitHub</h3>
                        <p>Tu caja fuerte en la nube</p>
                    </div>
                    
                    <div className="team-member">
                        <div className="team-icon supabase">
                            <svg viewBox="0 0 109 113" fill="currentColor">
                                <path d="M63.7076 110.284C60.8481 113.885 55.0502 111.912 54.9813 107.314L53.9738 40.0627L99.1935 40.0627C107.384 40.0627 111.952 49.5228 106.859 55.9374L63.7076 110.284Z"/>
                                <path d="M45.317 2.07103C48.1765 -1.53037 53.9745 0.442937 54.0434 5.041L54.4849 72.2922H9.83113C1.64038 72.2922 -2.92775 62.8321 2.1655 56.4175L45.317 2.07103Z"/>
                            </svg>
                        </div>
                        <h3>Supabase</h3>
                        <p>La memoria de tu aplicación</p>
                    </div>
                    
                    <div className="team-member">
                        <div className="team-icon vercel">
                            <svg viewBox="0 0 76 65" fill="currentColor">
                                <path d="M37.5274 0L75.0548 65H0L37.5274 0Z"/>
                            </svg>
                        </div>
                        <h3>Vercel</h3>
                        <p>Publica tu web al mundo</p>
                    </div>
                    
                    <div className="team-member">
                        <div className="team-icon editor">
                            <svg viewBox="0 0 24 24" fill="currentColor">
                                <path d="M13.325 3.05L8.667 20.432l1.932.518 4.658-17.382-1.932-.518zM7.612 18.36l1.36-1.448-.001-.019-5.094-4.78 5.095-4.779v-.017l-1.36-1.45-6.6 6.246 6.6 6.247zM16.388 18.36l6.6-6.246-6.6-6.247-1.36 1.45v.018l5.094 4.779-5.094 4.779.001.019 1.359 1.448z"/>
                            </svg>
                        </div>
                        <h3>Editor con IA</h3>
                        <p>Tu asistente que programa</p>
                    </div>
                </div>
            </section>

            {/* Steps */}
            <section className="gs-steps">
                <h2>Vamos paso a paso</h2>
                <p className="gs-steps-intro">
                    Haz clic en cada paso para ver las instrucciones. 
                    Cuando completes cada uno, márcalo y continuamos.
                </p>

                {/* Step 1: GitHub */}
                <div className={`gs-step ${steps.github.completed ? 'completed' : ''} ${expandedStep === 'github' ? 'expanded' : ''}`}>
                    <button 
                        className="gs-step-header"
                        onClick={() => setExpandedStep(expandedStep === 'github' ? null : 'github')}
                    >
                        <div className="step-number">1</div>
                        <div className="step-icon github">
                            <svg viewBox="0 0 24 24" fill="currentColor">
                                <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
                            </svg>
                        </div>
                        <div className="step-info">
                            <h3>Crear tu cuenta en GitHub</h3>
                            <p>Tu caja fuerte donde se guardará todo tu código</p>
                        </div>
                        <div className="step-status">
                            {steps.github.completed ? (
                                <span className="status-completed">✓ Completado</span>
                            ) : (
                                <span className="status-pending">Pendiente</span>
                            )}
                        </div>
                        <span className="step-expand">{expandedStep === 'github' ? '−' : '+'}</span>
                    </button>

                    {expandedStep === 'github' && (
                        <div className="gs-step-content">
                            <div className="step-explanation">
                                <h4>¿Qué es GitHub?</h4>
                                <p>
                                    Piensa en GitHub como <strong>una caja fuerte en la nube</strong> donde guardas 
                                    todo el código de tu proyecto. Si tu ordenador se rompe, si borras algo por error, 
                                    o si quieres trabajar desde otro ordenador... todo estará ahí, seguro.
                                </p>
                                <p>
                                    Además, GitHub es <strong>gratis</strong> para proyectos personales. 
                                    Miles de millones de desarrolladores en el mundo lo usan. Es el estándar.
                                </p>

                                <h4>¿Qué tienes que hacer?</h4>
                                <ol className="step-instructions">
                                    <li>
                                        <strong>Haz clic en el botón de abajo</strong> para ir a GitHub
                                    </li>
                                    <li>
                                        <strong>Crea tu cuenta</strong> con tu email. Elige un nombre de usuario que te guste 
                                        (será público, como @tunombre)
                                    </li>
                                    <li>
                                        <strong>Verifica tu email</strong> - te enviarán un código
                                    </li>
                                    <li>
                                        <strong>Vuelve aquí</strong> y marca el paso como completado
                                    </li>
                                </ol>

                                <div className="step-time">
                                    <span>⏱️ Tiempo estimado: 3-5 minutos</span>
                                </div>
                            </div>

                            <div className="step-actions">
                                <a 
                                    href="https://github.com/signup" 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="btn-go-to"
                                >
                                    Ir a GitHub →
                                </a>
                                
                                {!steps.github.completed && (
                                    <button 
                                        className="btn-mark-done"
                                        onClick={() => markCompleted('github')}
                                    >
                                        ✓ Ya tengo mi cuenta de GitHub
                                    </button>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                {/* Step 2: Supabase */}
                <div className={`gs-step ${steps.supabase.completed ? 'completed' : ''} ${expandedStep === 'supabase' ? 'expanded' : ''}`}>
                    <button 
                        className="gs-step-header"
                        onClick={() => setExpandedStep(expandedStep === 'supabase' ? null : 'supabase')}
                    >
                        <div className="step-number">2</div>
                        <div className="step-icon supabase">
                            <svg viewBox="0 0 109 113" fill="currentColor">
                                <path d="M63.7076 110.284C60.8481 113.885 55.0502 111.912 54.9813 107.314L53.9738 40.0627L99.1935 40.0627C107.384 40.0627 111.952 49.5228 106.859 55.9374L63.7076 110.284Z"/>
                                <path d="M45.317 2.07103C48.1765 -1.53037 53.9745 0.442937 54.0434 5.041L54.4849 72.2922H9.83113C1.64038 72.2922 -2.92775 62.8321 2.1655 56.4175L45.317 2.07103Z"/>
                            </svg>
                        </div>
                        <div className="step-info">
                            <h3>Crear tu cuenta en Supabase</h3>
                            <p>La memoria de tu aplicación, donde se guardan los datos</p>
                        </div>
                        <div className="step-status">
                            {steps.supabase.completed ? (
                                <span className="status-completed">✓ Completado</span>
                            ) : (
                                <span className="status-pending">Pendiente</span>
                            )}
                        </div>
                        <span className="step-expand">{expandedStep === 'supabase' ? '−' : '+'}</span>
                    </button>

                    {expandedStep === 'supabase' && (
                        <div className="gs-step-content">
                            <div className="step-explanation">
                                <h4>¿Qué es Supabase?</h4>
                                <p>
                                    Si GitHub guarda tu código, <strong>Supabase guarda los datos</strong> de tu aplicación. 
                                    Cuando un usuario se registra en tu app, cuando guarda algo, cuando escribe un mensaje... 
                                    todo eso se almacena en Supabase.
                                </p>
                                <p>
                                    Es como <strong>una hoja de Excel gigante en la nube</strong>, pero mucho más potente. 
                                    Y lo mejor: puedes crear tu cuenta <strong>con un solo clic usando GitHub</strong>.
                                </p>

                                <h4>¿Qué tienes que hacer?</h4>
                                <ol className="step-instructions">
                                    <li>
                                        <strong>Haz clic en el botón de abajo</strong> para ir a Supabase
                                    </li>
                                    <li>
                                        <strong>Haz clic en "Start your project"</strong>
                                    </li>
                                    <li>
                                        <strong>Elige "Continue with GitHub"</strong> - usarás la cuenta que acabas de crear
                                    </li>
                                    <li>
                                        <strong>Autoriza el acceso</strong> cuando te lo pida
                                    </li>
                                    <li>
                                        <strong>Vuelve aquí</strong> y marca el paso como completado
                                    </li>
                                </ol>

                                <div className="step-tip">
                                    <strong>💡 Consejo:</strong> No necesitas crear ningún proyecto ahora. 
                                    Solo la cuenta. Ya crearemos proyectos más adelante desde Roadmap.
                                </div>

                                <div className="step-time">
                                    <span>⏱️ Tiempo estimado: 2 minutos</span>
                                </div>
                            </div>

                            <div className="step-actions">
                                <a 
                                    href="https://supabase.com/dashboard" 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="btn-go-to"
                                >
                                    Ir a Supabase →
                                </a>
                                
                                {!steps.supabase.completed && (
                                    <button 
                                        className="btn-mark-done"
                                        onClick={() => markCompleted('supabase')}
                                    >
                                        ✓ Ya tengo mi cuenta de Supabase
                                    </button>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                {/* Step 3: Vercel */}
                <div className={`gs-step ${steps.vercel.completed ? 'completed' : ''} ${expandedStep === 'vercel' ? 'expanded' : ''}`}>
                    <button 
                        className="gs-step-header"
                        onClick={() => setExpandedStep(expandedStep === 'vercel' ? null : 'vercel')}
                    >
                        <div className="step-number">3</div>
                        <div className="step-icon vercel">
                            <svg viewBox="0 0 76 65" fill="currentColor">
                                <path d="M37.5274 0L75.0548 65H0L37.5274 0Z"/>
                            </svg>
                        </div>
                        <div className="step-info">
                            <h3>Crear tu cuenta en Vercel</h3>
                            <p>Para publicar tu web y que todo el mundo pueda verla</p>
                        </div>
                        <div className="step-status">
                            {steps.vercel.completed ? (
                                <span className="status-completed">✓ Completado</span>
                            ) : (
                                <span className="status-pending">Pendiente</span>
                            )}
                        </div>
                        <span className="step-expand">{expandedStep === 'vercel' ? '−' : '+'}</span>
                    </button>

                    {expandedStep === 'vercel' && (
                        <div className="gs-step-content">
                            <div className="step-explanation">
                                <h4>¿Qué es Vercel?</h4>
                                <p>
                                    Tu proyecto existe en tu ordenador y en GitHub, pero <strong>nadie más puede verlo</strong>. 
                                    Vercel es quien <strong>publica tu web en internet</strong> para que cualquier persona 
                                    del mundo pueda acceder a ella.
                                </p>
                                <p>
                                    Es como <strong>subir un video a YouTube</strong>: tu video está en tu ordenador, 
                                    pero cuando lo subes a YouTube, todo el mundo puede verlo. Vercel hace lo mismo con tu web.
                                </p>
                                <p>
                                    Y sí, también puedes <strong>conectar con GitHub en un clic</strong>.
                                </p>

                                <h4>¿Qué tienes que hacer?</h4>
                                <ol className="step-instructions">
                                    <li>
                                        <strong>Haz clic en el botón de abajo</strong> para ir a Vercel
                                    </li>
                                    <li>
                                        <strong>Haz clic en "Sign Up"</strong>
                                    </li>
                                    <li>
                                        <strong>Elige "Continue with GitHub"</strong>
                                    </li>
                                    <li>
                                        <strong>Autoriza el acceso</strong>
                                    </li>
                                    <li>
                                        <strong>Vuelve aquí</strong> y marca el paso como completado
                                    </li>
                                </ol>

                                <div className="step-time">
                                    <span>⏱️ Tiempo estimado: 2 minutos</span>
                                </div>
                            </div>

                            <div className="step-actions">
                                <a 
                                    href="https://vercel.com/signup" 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="btn-go-to"
                                >
                                    Ir a Vercel →
                                </a>
                                
                                {!steps.vercel.completed && (
                                    <button 
                                        className="btn-mark-done"
                                        onClick={() => markCompleted('vercel')}
                                    >
                                        ✓ Ya tengo mi cuenta de Vercel
                                    </button>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                {/* Step 4: Editor (Cursor o Antigravity) */}
                <div className={`gs-step ${steps.editor.completed ? 'completed' : ''} ${expandedStep === 'editor' ? 'expanded' : ''}`}>
                    <button 
                        className="gs-step-header"
                        onClick={() => setExpandedStep(expandedStep === 'editor' ? null : 'editor')}
                    >
                        <div className="step-number">4</div>
                        <div className="step-icon editor">
                            <svg viewBox="0 0 24 24" fill="currentColor">
                                <path d="M13.325 3.05L8.667 20.432l1.932.518 4.658-17.382-1.932-.518zM7.612 18.36l1.36-1.448-.001-.019-5.094-4.78 5.095-4.779v-.017l-1.36-1.45-6.6 6.246 6.6 6.247zM16.388 18.36l6.6-6.246-6.6-6.247-1.36 1.45v.018l5.094 4.779-5.094 4.779.001.019 1.359 1.448z"/>
                            </svg>
                        </div>
                        <div className="step-info">
                            <h3>Instalar tu editor con IA</h3>
                            <p>El asistente que te ayudará a programar (elige uno)</p>
                        </div>
                        <div className="step-status">
                            {steps.editor.completed ? (
                                <span className="status-completed">✓ {steps.editor.choice === 'cursor' ? 'Cursor' : 'Antigravity'}</span>
                            ) : (
                                <span className="status-pending">Pendiente</span>
                            )}
                        </div>
                        <span className="step-expand">{expandedStep === 'editor' ? '−' : '+'}</span>
                    </button>

                    {expandedStep === 'editor' && (
                        <div className="gs-step-content">
                            <div className="step-explanation">
                                <h4>¿Qué es un editor con IA?</h4>
                                <p>
                                    Aquí es donde ocurre la magia. Un editor con IA es <strong>como tener un programador 
                                    experto sentado a tu lado 24/7</strong>. Tú le dices qué quieres hacer en español, 
                                    y él escribe el código por ti.
                                </p>
                                <p>
                                    Tienes dos opciones. Elige la que prefieras:
                                </p>

                                <div className="editor-options">
                                    <div className="editor-option">
                                        <div className="editor-header">
                                            <h5>Cursor</h5>
                                            <span className="editor-tag">Recomendado para empezar</span>
                                        </div>
                                        <p>
                                            Una aplicación que instalas en tu ordenador. Muy potente, con IA integrada. 
                                            Tiene plan gratuito limitado y plan Pro por ~€20/mes para uso ilimitado.
                                        </p>
                                        <a 
                                            href="https://cursor.com" 
                                            target="_blank" 
                                            rel="noopener noreferrer"
                                            className="btn-go-to secondary"
                                        >
                                            Ir a Cursor →
                                        </a>
                                        {!steps.editor.completed && (
                                            <button 
                                                className="btn-mark-done"
                                                onClick={() => markCompleted('editor', 'cursor')}
                                            >
                                                ✓ He instalado Cursor
                                            </button>
                                        )}
                                    </div>

                                    <div className="editor-divider">
                                        <span>o</span>
                                    </div>

                                    <div className="editor-option">
                                        <div className="editor-header">
                                            <h5>Antigravity</h5>
                                            <span className="editor-tag">Genera proyectos completos</span>
                                        </div>
                                        <p>
                                            Funciona en el navegador. Le describes tu idea y genera el proyecto entero. 
                                            Ideal si quieres empezar rápido sin instalar nada.
                                        </p>
                                        <a 
                                            href="https://antigravity.dev" 
                                            target="_blank" 
                                            rel="noopener noreferrer"
                                            className="btn-go-to secondary"
                                        >
                                            Ir a Antigravity →
                                        </a>
                                        {!steps.editor.completed && (
                                            <button 
                                                className="btn-mark-done"
                                                onClick={() => markCompleted('editor', 'antigravity')}
                                            >
                                                ✓ Tengo cuenta en Antigravity
                                            </button>
                                        )}
                                    </div>
                                </div>

                                <div className="step-tip">
                                    <strong>💡 ¿Cuál elegir?</strong> Si nunca has programado, empieza con <strong>Cursor</strong>. 
                                    Es más guiado y te permite aprender mientras la IA te ayuda. 
                                    <strong>Antigravity</strong> es genial cuando ya tienes clara tu idea y quieres un prototipo rápido.
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </section>

            {/* Congratulations / Next Step */}
            {showCongrats ? (
                <section className="gs-congrats">
                    <div className="congrats-content">
                        <div className="congrats-icon">🎉</div>
                        <h2>¡Tu equipo está listo!</h2>
                        <p>
                            Has configurado todas las herramientas. Ahora sí, 
                            vamos a crear tu cuenta en <strong>Roadmap Platform</strong> 
                            y empezar a darle forma a tu proyecto.
                        </p>
                        <Link to="/register" className="btn-create-account">
                            Crear mi cuenta en Roadmap →
                        </Link>
                        <button className="btn-reset" onClick={resetProgress}>
                            Reiniciar progreso
                        </button>
                    </div>
                </section>
            ) : (
                <section className="gs-not-ready">
                    <div className="not-ready-content">
                        <p>
                            Completa los {4 - completedCount} paso{4 - completedCount !== 1 ? 's' : ''} restante{4 - completedCount !== 1 ? 's' : ''} para continuar
                        </p>
                    </div>
                </section>
            )}

            {/* Footer */}
            <footer className="gs-footer">
                <p>¿Tienes dudas? <Link to="/contact">Contáctanos</Link></p>
                <p className="gs-footer-note">
                    Todo este proceso es gratuito. Solo pagarás €2.99/mes por Roadmap Platform 
                    después del periodo de prueba.
                </p>
            </footer>
        </div>
    );
};

export default GettingStarted;
